import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const certifications = [
    {
      name: 'Microsoft Security MVP Candidate',
      icon: 'ri-shield-star-line',
      color: 'from-blue-500 to-blue-700',
    },
    {
      name: 'Azure Security Engineer',
      icon: 'ri-cloud-line',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      name: 'Microsoft 365 Security Admin',
      icon: 'ri-lock-line',
      color: 'from-emerald-500 to-teal-600',
    },
  ];

  const skills = [
    {
      category: 'SIEM & SOAR',
      items: [
        'Microsoft Sentinel',
        'KQL',
        'Log Analytics',
        'Playbook Automation',
      ],
    },
    {
      category: 'Endpoint Management',
      items: [
        'Microsoft Intune',
        'Compliance Policies',
        'Autopilot',
        'App Protection',
      ],
    },
    {
      category: 'Threat Detection',
      items: [
        'Microsoft Defender for Endpoint',
        'EDR',
        'Attack Surface Reduction',
        'Threat Hunting',
      ],
    },
    {
      category: 'Development',
      items: ['PowerShell', 'Microsoft Graph API', 'React', 'TypeScript'],
    },
  ];

  const timeline = [
    {
      year: '2024',
      title: 'IntuneVault Projesi',
      description:
        'Kapsamlı Intune yönetim platformunu geliştirdim ve açık kaynak olarak yayınladım.',
    },
    {
      year: '2024',
      title: 'Konferans Sunumları',
      description:
        'Microsoft Security konularında birden fazla konferansta sunum gerçekleştirdim.',
    },
    {
      year: '2023',
      title: 'Açık Kaynak Projeleri',
      description:
        'BitLocker ve Intune odaklı güvenlik araçlarını GitHub üzerinde topluluğa sundum.',
    },
    {
      year: '2023',
      title: 'Blog & İçerik Üretimi',
      description:
        'Microsoft Security ekosistemi hakkında teknik makaleler yazmaya başladım.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#0F1419] via-[#1A2332] to-[#0F1419]"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 40%, rgba(0, 120, 212, 0.12) 0%, transparent 50%),
                             radial-gradient(circle at 70% 70%, rgba(0, 120, 212, 0.08) 0%, transparent 50%),
                             repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.02) 2px, rgba(255,255,255,0.02) 4px)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30" />

        <div className="relative max-w-7xl mx-auto px-8 w-full">
          <div className="grid grid-cols-12 gap-12 items-center">
            {/* Profil Fotoğrafı */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="col-span-4 flex justify-center"
            >
              <div className="relative">
                <div className="w-64 h-64 rounded-2xl overflow-hidden border-4 border-white/10 shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/f3543b778985655566d8e7a649090a0e/b9559d23455b4863d6cf9d7896b8b582.png"
                    alt="Profil"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/40">
                  <i className="ri-shield-check-fill text-white text-2xl"></i>
                </div>
              </div>
            </motion.div>

            {/* Bilgiler */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="col-span-8 space-y-5"
            >
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-blue-500/30 rounded-lg">
                <span className="text-blue-400 text-sm font-medium">
                  Yazar Hakkında
                </span>
              </div>

              <h1 className="text-5xl font-bold text-white leading-tight">
                Özgür Çebi
              </h1>

              <p className="text-xl text-white/80 leading-relaxed max-w-2xl">
                Microsoft Security alanında çözümler üreten bir teknoloji tutkunu. Sentinel, EDR ve Intune mimarileri kuruyor, açık kaynak projelere destek veriyor ve deneyimlerini paylaşıyorum
              </p>

              <div className="flex items-center space-x-4 pt-2">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-white text-lg"></i>
                </a>
                <a
                  href="https://github.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-github-fill text-white text-lg"></i>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-fill text-white text-lg"></i>
                </a>
                <a
                  href="mailto:ozgur@ozgurcebi.com"
                  className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-mail-line text-white text-lg"></i>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Zaman Çizelgesi */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {[
                {
                  year: '2024',
                  title: 'IntuneVault Projesi',
                  description:
                    'Kapsamlı Intune yönetim platformunu geliştirdim ve açık kaynak olarak yayınladım.',
                  icon: 'ri-rocket-2-fill',
                  color: 'from-blue-500 to-cyan-500',
                },
                {
                  year: '2024',
                  title: 'Konferans Sunumları',
                  description:
                    'Microsoft Security konularında birden fazla konferansta sunum gerçekleştirdim.',
                  icon: 'ri-presentation-fill',
                  color: 'from-purple-500 to-pink-500',
                },
                {
                  year: '2023',
                  title: 'Açık Kaynak Projeleri',
                  description:
                    'BitLocker ve Intune odaklı güvenlik araçlarını GitHub üzerinde paylaştım.',
                  icon: 'ri-github-fill',
                  color: 'from-pink-500 to-rose-500',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="relative flex items-start space-x-6"
                >
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}
                    >
                      <i className={`${item.icon} text-white text-2xl`}></i>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Birlikte Çalışalım
            </h2>
            <p className="text-base text-gray-600 mb-8 leading-relaxed">
              Microsoft Security konularında eğitim veya işbirliği
              için benimle iletişime geçebilirsiniz.
            </p>
            <a
              href="mailto:ozgur@ozgurcebi.com"
              className="inline-block px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-mail-send-line mr-2"></i>
              İletişime Geç
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
